import {getState, postState} from "./state";
import _ from 'lodash';
import {
    getRelayTriggerSource,
    getRelayWorkType,
    getShadowRelayWorkType,
    getShadowTriggerSource
} from "../utils/relay-mode";
import {convertBitsToList, convertHoursListToBits} from "../utils/hours-conversion";
import moment from 'moment';
import {getRotation, putRotation} from "./rotation.service";
import {RelayPathIdTypes, RelayTypes} from "../enums/relayTypes";
import {convertTimeToMinutesAndSeconds} from "../utils/convertTimeToMinutesAndSeconds";
import {getCurrentTimestamp} from "../utils/currentTimestamp";
import {convertZeroToOne} from "../utils/convertZeroToOne";

const getRelayType = (id: string): RelayTypes => {
    if (id.includes(RelayPathIdTypes.RELAY)) {
        return RelayTypes.RELAY;
    } else if (id.includes(RelayPathIdTypes.IRDA)) {
        return RelayTypes.IRDA;
    }
    return RelayTypes.VOLTAGE_OUTPUT
}

const getRelayShadowName = (id: string) => {
    const relayType = getRelayType(id);
    switch (relayType) {
        case RelayTypes.RELAY:
            return RelayTypes.RELAY + id.replace(RelayPathIdTypes.RELAY, '') + 'SettingsId';
        case RelayTypes.IRDA:
            return RelayTypes.IRDA + id.replace(RelayPathIdTypes.IRDA, '') + 'SettingsId';
        case RelayTypes.VOLTAGE_OUTPUT:
        default:
            return RelayTypes.VOLTAGE_OUTPUT + 'SettingsId';
    }
}

const getAbstractRelay = (data: any, relayPathId: RelayPathIdTypes, relayType: RelayTypes, id: string) => {
    const relay = createRelayFromResponse(data, relayType, relayPathId, id);
    return createRelayObjectFromResponse(relay);
}

const createRelayFromResponse = (data: any, relayType: RelayTypes, relayPathId: RelayPathIdTypes, id: string) => {
    const key = id.replace(relayPathId, '');
    const payload = data.data
    const path = relayType === RelayTypes.VOLTAGE_OUTPUT ? 'state.desired.' + relayType + 'SettingsId' : 'state.desired.' + relayType + key + 'SettingsId';
    const reportedPath = relayType === RelayTypes.VOLTAGE_OUTPUT ? 'state.reported.' + relayType + 'SettingsId' : 'state.reported.' + relayType + key + 'SettingsId';
    return _.get(payload, path, _.get(payload, reportedPath, {}));
}

const createRelayObjectFromResponse = (relay: any) => {
    return {
        mode: getRelayWorkType(_.get(relay, 'WType', 0)),
        isActive: _.get(relay, 'Active', false),
        input: getRelayTriggerSource(_.get(relay, 'TSource', 0), getRelayWorkType(_.get(relay, 'WType', 0))),
        reactionTime: moment.utc(_.get(relay, 'WSett.ReactTime', 0)*1000).format('mm:ss'),
        holdingTime: moment.utc(_.get(relay, 'WSett.HoldTime', 0)*1000).format('mm:ss'),
        timeSettings: convertBitsToList(_.get(relay, 'WSett.PowerHours', []).map((item: string) => parseInt(item, 10))),
        temp1: _.get(relay, 'WSett.T1', '0'),
        temp2: _.get(relay, 'WSett.T2', '0'),
        voltageUp: _.get(relay, 'WSett.valueUp', '0'),
        voltageDown: _.get(relay, 'WSett.valueDown', '0'),
        hydrostat1: _.get(relay, 'WSett.H1', '0'),
        hydrostat2: _.get(relay, 'WSett.H2', '0'),
    }
}

export async function getRelay(id: string) {
    const relayType = getRelayType(id);
    return getState(getRelayShadowName(id)).then(
        (res: any) => {
            console.log('ok: ', res);
            console.log('relay type: ', relayType);
            switch (relayType) {
                case RelayTypes.RELAY:
                    return Promise.resolve(getAbstractRelay(res, RelayPathIdTypes.RELAY, RelayTypes.RELAY, id));
                case RelayTypes.VOLTAGE_OUTPUT:
                    return Promise.resolve(getAbstractRelay(res, RelayPathIdTypes.VOLTAGE_OUTPUT, RelayTypes.VOLTAGE_OUTPUT, id));
                case RelayTypes.IRDA:
                default:
                    return Promise.resolve(getAbstractRelay(res, RelayPathIdTypes.IRDA, RelayTypes.IRDA, id))
            }
        }
    ).catch(() => {
        return Promise.reject({});
    })
}

export const putRelay = (id: string, data: any, currentTimeMode: number, isActive: boolean) => {
    const convertedHours = convertHoursListToBits(_.get(data, 'timeSettings', {}), currentTimeMode).map((item: number) => item.toString());
    const mode = _.get(data, 'mode', 0);
    const relayType = getRelayType(id);
    console.log('id: ', id);
    if (relayType === RelayTypes.VOLTAGE_OUTPUT) {
        return postState(createAbstractRelayToSend(data, mode, id, convertedHours, isActive, RelayPathIdTypes.VOLTAGE_OUTPUT, RelayTypes.VOLTAGE_OUTPUT), 'VoltageOutputSettingsId');
    }
    return getRotation().then(
        (res: any) => {
            switch (relayType) {
                case RelayTypes.RELAY: {
                    const idOfDevice = parseInt(id.replace(RelayPathIdTypes.RELAY, ''), 10);
                    const airConditioners = res.airConditioners || [];
                    if (res.redundancy && isActive && airConditioners.some((ac: number) => ac === (idOfDevice - 1))) {
                        const rotationToSend = _.assign({}, res, {
                            redundancy: false
                        })
                        return Promise.all([putRotation(rotationToSend), postState(createAbstractRelayToSend(data, mode, id, convertedHours, isActive, RelayPathIdTypes.RELAY, RelayTypes.RELAY), getRelayShadowName(id))])
                    } else {
                        return Promise.all([postState(createAbstractRelayToSend(data, mode, id, convertedHours, isActive, RelayPathIdTypes.RELAY, RelayTypes.RELAY), getRelayShadowName(id))])
                    }
                }
                case RelayTypes.IRDA: {
                    const idOfDevice = parseInt(id.replace('irda', ''), 10);
                    const airConditioners = res.airConditioners || [];
                    if (res.redundancy && isActive && airConditioners.some((ac: number) => ac === (idOfDevice + 4))) {
                        const rotationToSend = _.assign({}, res, {
                            redundancy: false
                        })
                        return Promise.all([putRotation(rotationToSend), postState(createAbstractRelayToSend(data, mode, id, convertedHours, isActive, RelayPathIdTypes.IRDA, RelayTypes.IRDA), getRelayShadowName(id))])
                    } else {
                        console.log('jest')
                        return Promise.all([postState(createAbstractRelayToSend(data, mode, id, convertedHours, isActive, RelayPathIdTypes.IRDA, RelayTypes.IRDA), getRelayShadowName(id))])
                    }
                }
            }
        }
    )
}

const createAbstractRelayToSend = (data: any, mode: number, id: string, convertedHours: any, isActive: boolean, relayPathId: RelayPathIdTypes, relayType: RelayTypes) => {
    const key = id.replace(relayPathId, '');
    const relay = createAbstractRelayObject(data, mode, key, convertedHours, isActive, relayPathId, relayType);
    if (relayType === RelayTypes.VOLTAGE_OUTPUT) {
        return {
            [ relayType + 'SettingsId']: relay
        }
    }
    return {
        [ relayType + key + 'SettingsId']: relay
    }
}

const countId = (id: string, relayType: RelayTypes) => {
    switch (relayType) {
        case RelayTypes.RELAY:
            return parseInt(id, 10);
        case RelayTypes.IRDA:
            return parseInt(id, 10) + 5;
        default:
            return 10;
    }
}

const createAbstractRelayObject = (data: any, mode: number, id: string, convertedHours: any, isActive: boolean, relayPathId: RelayPathIdTypes, relayType: RelayTypes) => {
    if (relayType === RelayTypes.VOLTAGE_OUTPUT) {
        return {
            TSource: convertAnalogInput(_.get(data, 'input', 0)),
            Active: isActive,
            ONumber: 9,
            WSett: {
                valueUp: parseFloat(_.get(data, 'voltageUp', 0)),
                valueDown: parseFloat(_.get(data, 'voltageDown', 0))
            },
            timestamp: getCurrentTimestamp()
        }
    }
    return {
        ID: countId(id, relayType),
        Active: isActive,
        WType: getShadowRelayWorkType(_.get(data, 'mode', 0)),
        TSource: getShadowTriggerSource(_.get(data, 'input', 0), _.get(data, 'mode', 0)),
        ONumber: countId(id, relayType) - 1,
        WSett: {
            HoldTime: convertZeroToOne(convertTimeToMinutesAndSeconds(data, 'holdingTime')),
            ReactTime: convertZeroToOne(convertTimeToMinutesAndSeconds(data, 'reactionTime')),
            T1: parseInt(_.get(data, 'temp1', 0), 10),
            T2: parseInt(_.get(data, 'temp2', 0), 10),
            PowerHours: convertedHours,
            H2: parseFloat(_.get(data, 'hydrostat2', 0)),
            H1: parseFloat(_.get(data, 'hydrostat1', 0))
        },
        T1: parseInt(_.get(data, 'temp1', 0), 10),
        T2: parseInt(_.get(data, 'temp2', 0), 10),
        timestamp: getCurrentTimestamp()
    }
}


const convertAnalogInput = (index: number) => {
    return index + 6
}

export const createManualRelaysListFromShadow = (res: object) => {
    const data = _.get(res, 'state.desired.ManualSettings');
    return {
        'relay1': _.get(data, 'Relay1State', false),
        'relay2': _.get(data, 'Relay2State', false),
        'relay3': _.get(data, 'Relay3State', false),
        'relay4': _.get(data, 'Relay4State', false),
        'relay5': _.get(data, 'Relay5State', false),
        'irda1': _.get(data, 'Irda1State', false),
        'irda2': _.get(data, 'Irda2State', false),
        'irda3': _.get(data, 'Irda3State', false),
        'irda4': _.get(data, 'Irda4State', false),
        'analog1': _.get(data, 'VoltageOutState', false),
        'analog1Value': _.get(data, 'VolatgeOutValue', 0)
    }
}

export const createOneManualOptionToSave = (name: string, index: number, status: boolean) => {
    const currentTimestamp = getCurrentTimestamp();
    return {
        'ManualSettings': {
            [name.charAt(0).toUpperCase() + name.slice(1) + 'State']: status,
            [name.charAt(0).toUpperCase() + name.slice(1) + 'Timestamp']: currentTimestamp
        }
    }
}
export const createVoltageOneManualOptionToSave = (status: boolean, value: any) => {
    const currentTimestamp = getCurrentTimestamp();
    return {
        'ManualSettings': {
            VoltageOutState: status,
            VoltageOutTimestamp: currentTimestamp,
            VolatgeOutValue: value
        }
    }
}
export const createManualRelaysListToSave = (data: object) => {
    const currentTimestamp = getCurrentTimestamp();
    return {
        'ManualSettings': {
            'Relay1State': _.get(data, 'relay1'),
            'Relay2State': _.get(data, 'relay2'),
            'Relay3State': _.get(data, 'relay3'),
            'Relay4State': _.get(data, 'relay4'),
            'Relay5State': _.get(data, 'relay5'),
            'Irda1State': _.get(data, 'irda1'),
            'Irda2State': _.get(data, 'irda2'),
            'Irda3State': _.get(data, 'irda3'),
            'Irda4State': _.get(data, 'irda4'),
            'VoltageOutState': _.get(data, 'analog1'),
            'VolatgeOutValue': parseInt(_.get(data, 'analog1Value', '0'), 10),
            'Relay1Timestamp': currentTimestamp,
            'Relay2Timestamp': currentTimestamp,
            'Relay3Timestamp': currentTimestamp,
            'Relay4Timestamp': currentTimestamp,
            'Relay5Timestamp': currentTimestamp,
            'Irda1Timestamp': currentTimestamp,
            'Irda2Timestamp': currentTimestamp,
            'Irda3Timestamp': currentTimestamp,
            'Irda4Timestamp': currentTimestamp,
            'VoltageOutTimestamp': currentTimestamp
        }
    }
}

export const createListOfRelaysNames = (obj: object) => {
    const list: string[] = [];
    Object.keys(obj).forEach((key) => {
        const value = _.get(obj, key, false);
        if (value === true) {
            if (key.includes('relay')) {
                const index = key.replace('relay', '');
                list.push('Relay' + index + 'SettingsId');
            } else if (key.includes('irda')) {
                const index = key.replace('irda', '');
                list.push('Irda' + index + 'SettingsId');
            } else if (key.includes('analog1')) {
                list.push('VoltageOutputSettingsId')
            }
        }
    })
    return list;
}
